import { ActiveScriptCardUnit, CardOption, PassStep } from '@/models/scripts'
import router, { RouteNames } from '@/router'
import scriptsApiService from '@/services/scripts'
import { SCRIPTS__FETCH_ONE } from '@/store/actions'
import {
  ActiveScriptHistoryAddStepMutationPayload,
  ActiveScriptMutationTypes,
  ActiveScriptSetStartTimeMutationPayload,
  ActiveScriptStartCardMutationPayload,
  ActiveScriptStartMutationPayload,
} from './mutations'
import { ActiveScriptState } from './state'
import { CardOptionType } from '@/constants/scripts'
import { RootState } from '@/store/state'
import _find from 'lodash.find'
import { ActionTree } from 'vuex'

export enum ActiveScriptActionTypes {
  Start = '[Active Script Action] Start',
  Continue = '[Active Script Action] Continue',
  Move = '[Active Script Action] Move',
  Back = '[Active Script Action] Back',
  Finish = '[Active Script Action] Finish',
}

export type ActiveScriptStartPayload = {
  scriptId: string
}

export type ActiveScriptBackPayload = { to: number }

export type ActiveScriptFinishPayload = { scriptId: string; reason: string }

export type ActiveScriptMovePayload = {
  card: ActiveScriptCardUnit
  option: CardOption
}

export const actions: ActionTree<ActiveScriptState, RootState> = {
  async [ActiveScriptActionTypes.Start](
    context,
    payload: ActiveScriptStartPayload,
  ) {
    const { scriptId } = payload
    const response = await scriptsApiService.usageStart(scriptId)

    const startMutationPayload: ActiveScriptStartMutationPayload = { scriptId }
    context.commit(ActiveScriptMutationTypes.Start, startMutationPayload)

    const startCardPayload: ActiveScriptStartCardMutationPayload = {
      scriptId,
      card: response.data,
    }
    context.commit(ActiveScriptMutationTypes.StartCard, startCardPayload)

    return response
  },

  async [ActiveScriptActionTypes.Continue](context) {
    const { scriptId } = context.state

    if (scriptId) {
      // Check if there is no active script in the store
      if (context.getters.getScriptById(scriptId) === undefined) {
        await context.dispatch(SCRIPTS__FETCH_ONE, { id: scriptId })
      }

      const response = await scriptsApiService.usageContinue(scriptId)

      const startMutationPayload: ActiveScriptStartMutationPayload = {
        scriptId,
      }
      context.commit(ActiveScriptMutationTypes.Start, startMutationPayload)

      const startTimeMutationPayload: ActiveScriptSetStartTimeMutationPayload = {
        started: response.data?.started,
      }
      context.commit(
        ActiveScriptMutationTypes.SetStartTime,
        startTimeMutationPayload,
      )

      const startCardPayload: ActiveScriptStartCardMutationPayload = {
        scriptId,
        card: response?.data,
        history: response?.data?.history,
      }
      context.commit(ActiveScriptMutationTypes.StartCard, startCardPayload)

      return response
    }
  },

  async [ActiveScriptActionTypes.Move](
    context,
    payload: ActiveScriptMovePayload,
  ) {
    const scriptId = context.getters.getActiveScriptId

    const { card, option } = payload
    const { n } = option

    const selectedOption = _find<PassStep>(
      context.getters.getActiveScriptHistory,
      step => step.card === card.num && step.opt === option.n,
    )

    if (selectedOption) {
      const payload: ActiveScriptBackPayload = { to: selectedOption.step + 1 }
      return context.dispatch(ActiveScriptActionTypes.Back, payload)
    }

    const passedStep = context.getters.getHistoryStepByCardNum(card.num)

    const response = await scriptsApiService.usageMove(scriptId, {
      from: passedStep?.step,
      by: n,
    })

    if (response?.data?.type === CardOptionType.Final) {
      context.commit(ActiveScriptMutationTypes.Finish)

      return router.push({ name: RouteNames.ScriptsPage })
    }

    if (response?.data?.type === CardOptionType.Script && option.link) {
      const startMutationPayload: ActiveScriptStartMutationPayload = {
        scriptId: String(option.link),
      }
      context.commit(ActiveScriptMutationTypes.Start, startMutationPayload)
      context.dispatch(ActiveScriptActionTypes.Continue)
    }

    const addStepPayload: ActiveScriptHistoryAddStepMutationPayload = {
      fromCard: card,
      card: response.data,
      option,
    }
    context.commit(ActiveScriptMutationTypes.HistoryAddStep, addStepPayload)

    const startCardPayload: ActiveScriptStartCardMutationPayload = {
      scriptId,
      card: response.data,
    }

    context.commit(ActiveScriptMutationTypes.StartCard, startCardPayload)

    return response
  },

  async [ActiveScriptActionTypes.Back](
    context,
    payload: ActiveScriptBackPayload,
  ) {
    const scriptId = context.getters.getActiveScriptId

    const { to } = payload

    const response = await scriptsApiService.usageBack(scriptId, { to })

    const startCardPayload: ActiveScriptStartCardMutationPayload = {
      scriptId,
      card: response.data,
    }

    context.commit(ActiveScriptMutationTypes.StartCard, startCardPayload)

    return response
  },

  async [ActiveScriptActionTypes.Finish](
    context,
    payload: ActiveScriptFinishPayload,
  ) {
    const { scriptId, reason } = payload
    const response = await scriptsApiService.usageFinish(scriptId, reason)

    context.commit(ActiveScriptMutationTypes.Finish)

    return response
  },
}
